/**
 * Gender
 */
export const GENDER = {
  female: 'female',
  male: 'male',
  doesntMatter: 'doesnt_matter',
} as const;
export type Gender = (typeof GENDER)[keyof typeof GENDER];

const GENDER_LABEL = {
  [GENDER.female]: 'Female',
  [GENDER.male]: 'Male',
  [GENDER.doesntMatter]: "Doesn't matter",
};

export const getGenderLabel = (gender: Gender) => GENDER_LABEL[gender] || '';

/**
 * Gender tone
 */
export const GENDER_TONE = {
  feminine: 'feminine',
  neutral: 'neutral',
  masculine: 'masculine',
} as const;
export type GenderTone = (typeof GENDER_TONE)[keyof typeof GENDER_TONE];

const DEFAULT_GENDER_TONE = GENDER_TONE.neutral;

const GENDER_TONE_PROPS = {
  [GENDER_TONE.feminine]: { title: 'Female tone of voice' },
  [GENDER_TONE.masculine]: { title: 'Male tone of voice' },
  [GENDER_TONE.neutral]: { title: 'Gender-neutral tone of voice' },
};

export const getGenderToneTitle = (genderTone: GenderTone) => {
  const alignedTone = genderTone || DEFAULT_GENDER_TONE;
  return GENDER_TONE_PROPS[alignedTone]?.title || '';
};

/**
 * Age groups
 */
export const AGE_GROUPS = [
  {
    localValue: 0,
    bucketEnd: 13,
    bucketStart: 13,
    label: '13',
  },
  {
    localValue: 1,
    bucketEnd: 24,
    bucketStart: 25,
    label: '25',
  },
  {
    localValue: 2,
    bucketEnd: 44,
    bucketStart: 45,
    label: '45',
  },
  {
    localValue: 3,
    bucketEnd: 65,
    bucketStart: 65,
    label: '65+',
  },
];

export const MIN_AGE_GROUP = AGE_GROUPS[0];
export const MAX_AGE_GROUP = AGE_GROUPS[AGE_GROUPS.length - 1];

export const getAgeGroupLabel = (value: number) => {
  const ageGroup = AGE_GROUPS.find((a) => a.bucketEnd === value);
  return ageGroup ? ageGroup.label : value;
};

export const AGE_GROUP_APPEAL = {
  youngest: 'youngest',
  younger: 'younger',
  oldest: 'oldest',
  older: 'older',
  middle: 'middle',
  neutral: 'neutral',
  none: 'none',
} as const;
export type AgeGroupAppeal = (typeof AGE_GROUP_APPEAL)[keyof typeof AGE_GROUP_APPEAL];

export const getAgeGroupAppealTitle = (
  score: number,
  noneAppeal: boolean,
  ageGroupAppeal: AgeGroupAppeal
) => {
  if (!score) {
    return 'Not appealing to any age group';
  }

  const getAppealAgeTitle = (appeal: AgeGroupAppeal) => {
    switch (appeal) {
      case AGE_GROUP_APPEAL.youngest:
        return 'Appealing to younger age groups';
      case AGE_GROUP_APPEAL.oldest:
        return 'Appealing to older age groups';
      case AGE_GROUP_APPEAL.middle:
        return 'Appealing to young and middle-age adults';
      case AGE_GROUP_APPEAL.none:
      case AGE_GROUP_APPEAL.neutral:
      default:
        return 'Appealing to all age groups';
    }
  };

  const getNoneAppealAgeTitle = (appeal: AgeGroupAppeal) => {
    switch (appeal) {
      case AGE_GROUP_APPEAL.youngest:
        return 'Somewhat appealing to younger age groups';
      case AGE_GROUP_APPEAL.oldest:
        return 'Somewhat appealing to older age groups';
      case AGE_GROUP_APPEAL.middle:
        return 'Somewhat appealing to young and middle-age adults';
      case AGE_GROUP_APPEAL.none:
      case AGE_GROUP_APPEAL.neutral:
      default:
        return 'Somewhat appealing to all age groups';
    }
  };

  return noneAppeal ? getNoneAppealAgeTitle(ageGroupAppeal) : getAppealAgeTitle(ageGroupAppeal);
};
